import { useSheet } from '../app/ui';
import { canAddScreen } from '@noorview/core';
import { useApp } from '../data/store';
import { SheetClosing } from '../ui/controls';
import { ScheduleSheet } from './Schedule';
import { AddScreenSheet, PairSheet } from './Pair';
import { CsvSheet } from './Csv';
import { EidSheet, JumuahSheet } from './Times';
import { SearchSheet } from './Search';
import { UpgradeSheet } from './Upgrade';
import { Fullscreen } from './Fullscreen';
import { LogoSheet } from './Logo';
import { AppealSheet, EditBroadcastSheet, MessageSheet } from './Broadcast';
import { KhutbahSheet } from './Khutbah';
import { AnalyticsSheet } from './Analytics';
import { AnnounceSheet } from './Announce';

export function SheetHost() {
  const sheet = useSheet((s) => s.sheet);
  const close = useSheet((s) => s.close);
  const closing = useSheet((s) => s.closing);
  if (!sheet) return null;
  return <SheetClosing.Provider value={closing}>{body(sheet, close)}</SheetClosing.Provider>;
}

function body(sheet: NonNullable<ReturnType<typeof useSheet.getState>['sheet']>, close: () => void) {
  switch (sheet.name) {
    case 'schedule': return <ScheduleSheet screenId={sheet.screenId} onClose={close} />;
    case 'pair': return freeScreenBlocked(sheet.screenId) ? <UpgradeSheet reason="screens" onClose={close} /> : <PairSheet screenId={sheet.screenId} onClose={close} />;
    case 'addScreen': return atScreenLimit() ? <UpgradeSheet reason="screens" onClose={close} /> : <AddScreenSheet onClose={close} />;
    case 'csv': return <CsvSheet onClose={close} />;
    case 'jumuah': return <JumuahSheet onClose={close} />;
    case 'eid': return <EidSheet onClose={close} />;
    case 'search': return <SearchSheet onClose={close} />;
    case 'upgrade': return <UpgradeSheet reason={sheet.reason} onClose={close} />;
    case 'logo': return <LogoSheet first={sheet.first} onClose={close} />;
    case 'message': return <MessageSheet onClose={close} />;
    case 'appeal': return <AppealSheet onClose={close} />;
    case 'khutbah': return <KhutbahSheet onClose={close} />;
    case 'editBroadcast': return <EditBroadcastSheet id={sheet.id} back={sheet.back} onClose={close} />;
    case 'announce': return <AnnounceSheet screenId={sheet.screenId} widgetId={sheet.widgetId} onClose={close} />;
    case 'analytics': return <AnalyticsSheet onClose={close} />;
    case 'fullscreen': return <Fullscreen screenId={sheet.screenId} design={sheet.design} label={sheet.label} onClose={close} />;
  }
}

/* The Free plan is one screen: every Add Screen button (dashboard, Screens, Studio, search) lands here, so on Free with
   a screen already they all show the plans instead. The database refuses it too (free_plan_limits). */
function atScreenLimit() {
  const ws = useApp.getState().ws;
  return !!ws && !canAddScreen(ws.masjid.plan, ws.screens.length);
}
/** On Free, only the masjid's first screen can be paired with a display (a masjid that moved to Free keeps its others,
 *  but can't connect them). Pairing without a chosen screen is allowed: the sheet only offers screens it may pair. */
function freeScreenBlocked(screenId?: string) {
  const ws = useApp.getState().ws;
  if (!ws || ws.masjid.plan !== 'free' || !screenId || ws.screens.length < 2) return false;
  // Screens come oldest first (ordered by created_at).
  return ws.screens[0]?.id !== screenId;
}
